import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FavoritePriceShowAs,
  IFavoriteLocationExtension,
  IFavoriteMonthExtension,
  IFavoritePeriod,
  IFavoritePriceValue,
  ModeValueRow,
  MonthHalf,
} from "../../../../types/IFavoriteOrigins";
import { MONTHS_LOCAL } from "../../../../types/IMonth";
import { getRowKey, sortMonthsByTimeline } from "../FavoriteOriginHelper";
import { parsePriceInput } from "../priceFormatting";
import {
  persistPeriodPrice,
  persistSplitStateDelta,
} from "../originManagerCrudActions";

const MONTHS = MONTHS_LOCAL;

interface UseOriginManagerPriceEditingParams {
  selectedProduct: string;
  selectedMonths: string[];
  currentMonth: number;
  rows: IFavoriteLocationExtension[];
  filteredRows: IFavoriteLocationExtension[];
  newlyAddedOrigins: Record<string, IFavoriteLocationExtension[]>;
  setNewlyAddedOriginsByProduct: React.Dispatch<
    React.SetStateAction<Record<string, IFavoriteLocationExtension[]>>
  >;
  setEditingNewlyAddedIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  setJustAddedByProduct: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  getModeRows: (
    origin: IFavoriteLocationExtension,
    monthChip?: string,
    half?: MonthHalf,
  ) => ModeValueRow[];
  getMarketerIdentifier: () => string;
  refreshFavorites: (marketer: string) => Promise<any[]>;
  onSaveComplete?: () => void;
  onProductSelect: (product: string) => void;
  failedNotification: (title: string, message: string) => void;
  trackBackgroundOp: <T>(operation: Promise<T>) => Promise<T>;
  savedOrigins: IFavoriteLocationExtension[];
  deletedRowKeysByProduct: Record<string, Set<string>>;
  savePendingOrderInBackground: (
    productKey: string,
    savedOrigins: IFavoriteLocationExtension[],
    newlyAddedOrigins: Record<string, IFavoriteLocationExtension[]>,
    deletedRowKeysByProduct: Record<string, Set<string>>,
  ) => void;
  setPendingOrderSaveByProduct: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  originManagerUnsavedNavAttemptSource: "search" | "close" | null;
  triggerOriginManagerUnsavedNavResolution: (
    action: "save" | "discard",
  ) => void;
  triggerOriginManagerCloseRequest: () => void;
  setOriginManagerLiveSplitMonthKeys: (
    product: string,
    monthKeys: string[],
  ) => void;
  clearOriginManagerLiveSplitMonthKeys: (product: string) => void;
  panelRef: React.RefObject<HTMLDivElement>;
}

// Owns all price-editing state: drafts, editing row/product keys, split-month
// tracking, and the save/discard/done flows that persist edits to the server.
export const useOriginManagerPriceEditing = ({
  selectedProduct,
  selectedMonths,
  currentMonth,
  rows,
  filteredRows,
  newlyAddedOrigins,
  setNewlyAddedOriginsByProduct,
  setEditingNewlyAddedIds,
  setJustAddedByProduct,
  getModeRows,
  getMarketerIdentifier,
  refreshFavorites,
  onSaveComplete,
  onProductSelect,
  failedNotification,
  trackBackgroundOp,
  savedOrigins,
  deletedRowKeysByProduct,
  savePendingOrderInBackground,
  setPendingOrderSaveByProduct,
  originManagerUnsavedNavAttemptSource,
  triggerOriginManagerUnsavedNavResolution,
  triggerOriginManagerCloseRequest,
  setOriginManagerLiveSplitMonthKeys,
  clearOriginManagerLiveSplitMonthKeys,
  panelRef,
}: UseOriginManagerPriceEditingParams) => {
  const [editedPricesByProduct, setEditedPricesByProduct] = useState<
    Record<string, Record<string, string>>
  >({});
  const [editedPriceShowAsByProduct, setEditedPriceShowAsByProduct] = useState<
    Record<string, Record<string, FavoritePriceShowAs>>
  >({});
  const [editedSplitMonthsByProduct, setEditedSplitMonthsByProduct] = useState<
    Record<string, Record<string, string[]>>
  >({});
  const [isEditingPrices, setIsEditingPrices] = useState(false);
  const [editingProductKey, setEditingProductKey] = useState<string | null>(
    null,
  );
  const [draftPrices, setDraftPrices] = useState<Record<string, string>>({});
  const [draftPriceShowAs, setDraftPriceShowAs] = useState<
    Record<string, FavoritePriceShowAs>
  >({});
  const [recentlySavedKeys, setRecentlySavedKeys] = useState<Set<string>>(
    new Set(),
  );
  const [editingSplitMonths, setEditingSplitMonths] = useState<string[]>([]);
  const [editingRowKey, setEditingRowKey] = useState<string | null>(null);
  const [activeCellKey, setActiveCellKey] = useState<string | null>(null);
  // Tracks unsaved split months per row during the current edit session (cleared on Revert/Done).
  const [pendingSplitMonthsByRow, setPendingSplitMonthsByRow] = useState<
    Record<string, string[]>
  >({});
  const [pendingRowKey, setPendingRowKey] = useState<string | null>(null);
  const [pendingProductKey, setPendingProductKey] = useState<string | null>(
    null,
  );
  const [showSaveDiscard, setShowSaveDiscard] = useState(false);
  const priceInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const dirtyDraftPricesRef = useRef<Record<string, string>>({});
  const dirtyDraftPriceShowAsRef = useRef<Record<string, FavoritePriceShowAs>>(
    {},
  );
  const dirtyPriceKeysRef = useRef<Set<string>>(new Set());

  // scoped at product level
  const getPriceInputKey = useCallback(
    (
      origin: IFavoriteLocationExtension,
      modeKey: string,
      month: string,
      half: MonthHalf = "full",
    ) =>
      `${origin.competitor ? "competitor" : "koch"}-${
        origin.location.id
      }-${modeKey}-${month}-${half}-${selectedProduct}`,
    [selectedProduct],
  );

  const getPriceInputValue = useCallback(
    (
      origin: IFavoriteLocationExtension,
      modeRow: ModeValueRow,
      month: string,
      half: MonthHalf = "full",
    ) => {
      const isEditingCurrentProduct =
        isEditingPrices && editingProductKey === selectedProduct;
      const editedPrices = editedPricesByProduct[selectedProduct] ?? {};
      const key = getPriceInputKey(origin, modeRow.key, month, half);
      const activeShowAs =
        (isEditingCurrentProduct ? draftPriceShowAs[key] : undefined) ??
        (editedPriceShowAsByProduct[selectedProduct] ?? {})[key] ??
        modeRow.showAs;
      if (activeShowAs === "DISCUSS") {
        return "Discuss";
      }
      if (activeShowAs === "NO_OFFER") {
        return "No Offer";
      }
      if (isEditingCurrentProduct && draftPrices[key] !== undefined) {
        return draftPrices[key];
      }
      if (editedPrices[key] !== undefined) {
        return editedPrices[key];
      }
      return modeRow.value === null || modeRow.value === undefined
        ? ""
        : String(modeRow.value);
    },
    [
      draftPriceShowAs,
      draftPrices,
      editedPriceShowAsByProduct,
      editedPricesByProduct,
      editingProductKey,
      getPriceInputKey,
      isEditingPrices,
      selectedProduct,
    ],
  );

  const getPriceShowAsValue = useCallback(
    (
      origin: IFavoriteLocationExtension,
      modeRow: ModeValueRow,
      month: string,
      half: MonthHalf = "full",
    ): FavoritePriceShowAs | null => {
      const key = getPriceInputKey(origin, modeRow.key, month, half);
      const isEditingCurrentProduct =
        isEditingPrices && editingProductKey === selectedProduct;
      const draftShowAs = isEditingCurrentProduct
        ? draftPriceShowAs[key]
        : undefined;
      const persistedShowAs = (editedPriceShowAsByProduct[selectedProduct] ??
        {})[key];
      const resolved = draftShowAs ?? persistedShowAs ?? modeRow.showAs;
      // Competitor origins only support PRICE and NO_OFFER in the editor.
      if (origin.competitor && resolved === "DISCUSS") {
        return "PRICE";
      }
      return resolved;
    },
    [
      draftPriceShowAs,
      editedPriceShowAsByProduct,
      editingProductKey,
      getPriceInputKey,
      isEditingPrices,
      selectedProduct,
    ],
  );

  const getDisplayPriceValue = useCallback(
    (
      origin: IFavoriteLocationExtension,
      modeRow: ModeValueRow,
      month: string,
      half: MonthHalf = "full",
    ): number | null => {
      const editedPrices = editedPricesByProduct[selectedProduct] ?? {};
      const key = getPriceInputKey(origin, modeRow.key, month, half);
      const isEditingCurrentProduct =
        isEditingPrices && editingProductKey === selectedProduct;
      if (isEditingCurrentProduct && draftPrices[key] !== undefined) {
        return parsePriceInput(draftPrices[key]);
      }
      const edited = editedPrices[key];
      if (edited !== undefined) {
        return parsePriceInput(edited);
      }
      const resolvedModeRow =
        getModeRows(origin, month, half).find(
          (row) => row.key === modeRow.key,
        ) ?? modeRow;
      return resolvedModeRow.value;
    },
    [
      draftPrices,
      editedPricesByProduct,
      editingProductKey,
      getModeRows,
      getPriceInputKey,
      isEditingPrices,
      selectedProduct,
    ],
  );

  const handlePriceInputChange = useCallback(
    (
      origin: IFavoriteLocationExtension,
      modeKey: string,
      month: string,
      half: MonthHalf,
      value: string,
    ) => {
      const priceKey = getPriceInputKey(origin, modeKey, month, half);
      dirtyDraftPricesRef.current[priceKey] = value;
      dirtyPriceKeysRef.current.add(priceKey);
      setRecentlySavedKeys((prev) => {
        const next = new Set(prev);
        next.add(priceKey);
        return next;
      });
      if (!isEditingPrices || editingProductKey !== selectedProduct) {
        // Editing mode hasn't been committed to state yet (React batching) or
        // the product key is stale — start it inline so the typed value is never lost.
        setEditingProductKey(selectedProduct);
        setIsEditingPrices(true);
        setDraftPrices((prev) => ({
          ...prev,
          [priceKey]: value,
        }));
        return;
      }
      setDraftPrices((prev) => ({
        ...prev,
        [priceKey]: value,
      }));
    },
    [editingProductKey, getPriceInputKey, isEditingPrices, selectedProduct],
  );

  // TODO: rename field to date instead of month
  const getSplitMonthsFromOrigin = useCallback(
    (origin: IFavoriteLocationExtension) => {
      if (origin.competitor) {
        return [];
      }
      return origin.months
        .filter((date) => date.split)
        .map((date) => MONTHS[date.parsedMonth.month - 1])
        .filter(Boolean);
    },
    [],
  );

  // Restore any unsaved splits set during this session, entering `origin`'s row.
  const startEditingRow = useCallback(
    (
      origin: IFavoriteLocationExtension,
      options?: { preserveDrafts?: boolean },
    ) => {
      const rowKey = getRowKey(origin);

      // Save the departing row's unsaved splits so the header persists.
      if (editingRowKey && editingRowKey !== rowKey) {
        setPendingSplitMonthsByRow((prev) => ({
          ...prev,
          [editingRowKey]: editingSplitMonths,
        }));
      }

      const persistedSplitMonths =
        pendingSplitMonthsByRow[rowKey] ??
        editedSplitMonthsByProduct[selectedProduct]?.[rowKey] ??
        getSplitMonthsFromOrigin(origin);
      setEditingRowKey(rowKey);
      setEditingProductKey(selectedProduct);
      if (!options?.preserveDrafts) {
        setDraftPrices(editedPricesByProduct[selectedProduct] ?? {});
        setDraftPriceShowAs(editedPriceShowAsByProduct[selectedProduct] ?? {});
        dirtyDraftPricesRef.current = {};
        dirtyDraftPriceShowAsRef.current = {};
        dirtyPriceKeysRef.current = new Set();
      }
      setEditingSplitMonths(persistedSplitMonths);
      setIsEditingPrices(true);
    },
    [
      editedPriceShowAsByProduct,
      editedPricesByProduct,
      editedSplitMonthsByProduct,
      editingRowKey,
      editingSplitMonths,
      getSplitMonthsFromOrigin,
      pendingSplitMonthsByRow,
      selectedProduct,
    ],
  );

  const handlePriceCellClick = useCallback(
    (
      origin: IFavoriteLocationExtension,
      modeKey: string,
      month: string,
      half: MonthHalf = "full",
    ) => {
      const priceKey = getPriceInputKey(origin, modeKey, month, half);
      setActiveCellKey(priceKey);
      const rowKey = getRowKey(origin);
      if (editingRowKey === rowKey) return;

      startEditingRow(origin, {
        preserveDrafts:
          isEditingPrices && editingProductKey === selectedProduct,
      });
    },
    [
      editingProductKey,
      editingRowKey,
      getPriceInputKey,
      isEditingPrices,
      selectedProduct,
      startEditingRow,
    ],
  );

  useEffect(() => {
    if (!editingRowKey || !activeCellKey) return;
    const input = priceInputRefs.current[activeCellKey];
    if (input && document.activeElement !== input) input.focus();
  }, [editingRowKey, activeCellKey]);

  const handlePriceInputKeyDown = useCallback(
    (
      event: React.KeyboardEvent<HTMLInputElement>,
      origin: IFavoriteLocationExtension,
      modeKey: string,
      column: { month: string; half: MonthHalf; isSplit: boolean },
      rowColumns: Array<{ month: string; half: MonthHalf; isSplit: boolean }>,
      columnModeRows: ModeValueRow[],
    ) => {
      if (event.key !== "Tab" && event.key !== "Enter") return;

      if (event.key === "Enter") {
        event.preventDefault();
        const currentModeIndex = columnModeRows.findIndex(
          (row) => row.key === modeKey,
        );
        const isLastMode = currentModeIndex === columnModeRows.length - 1;

        if (!isLastMode) {
          // Move down to the next mode row in the same column
          const nextMode = columnModeRows[currentModeIndex + 1];
          const targetKey = getPriceInputKey(
            origin,
            nextMode.key,
            column.month,
            column.half,
          );
          const targetInput = priceInputRefs.current[targetKey];
          if (targetInput) {
            targetInput.focus();
          }
        } else {
          // Last mode of this origin — jump to the first mode of the next origin in the same column
          const currentRowIdx = rows.findIndex(
            (r) => getRowKey(r) === getRowKey(origin),
          );
          const nextOrigin = rows[currentRowIdx + 1];
          if (nextOrigin) {
            const nextModeRows = getModeRows(
              nextOrigin,
              column.month,
              column.half,
            );
            if (nextModeRows.length > 0) {
              const nextRowKey = getRowKey(nextOrigin);
              const priceKey = getPriceInputKey(
                nextOrigin,
                nextModeRows[0].key,
                column.month,
                column.half,
              );
              setActiveCellKey(priceKey);
              if (editingRowKey !== nextRowKey) {
                startEditingRow(nextOrigin, {
                  preserveDrafts:
                    isEditingPrices && editingProductKey === selectedProduct,
                });
              }
            }
          }
        }
        return;
      }

      const currentModeIndex = columnModeRows.findIndex(
        (row) => row.key === modeKey,
      );
      const currentColIndex = rowColumns.findIndex(
        (col) => col.month === column.month && col.half === column.half,
      );

      const isLastMode = currentModeIndex === columnModeRows.length - 1;
      const isFirstMode = currentModeIndex === 0;
      const isLastColumn = currentColIndex === rowColumns.length - 1;
      const isFirstColumn = currentColIndex === 0;

      // For competitor origins: when reaching the edge of a row, jump to the
      // adjacent competitor origin instead of falling through to browser tab.
      if (origin.competitor) {
        const currentRowIdx = rows.findIndex(
          (r) => getRowKey(r) === getRowKey(origin),
        );

        if (!event.shiftKey && isLastMode && isLastColumn) {
          // Forward: go to first cell of next competitor origin (first month, first mode)
          const nextOrigin = rows
            .slice(currentRowIdx + 1)
            .find((r) => r.competitor);
          if (nextOrigin) {
            const nextModeRows = getModeRows(nextOrigin);
            if (nextModeRows.length > 0) {
              const targetKey = getPriceInputKey(
                nextOrigin,
                nextModeRows[0].key,
                selectedMonths[0],
                "full",
              );
              const targetInput = priceInputRefs.current[targetKey];
              if (targetInput) {
                event.preventDefault();
                targetInput.focus();
              }
            }
          }
          return;
        }

        if (event.shiftKey && isFirstMode && isFirstColumn) {
          // Backward: go to last cell of previous competitor origin (last month, last mode)
          const prevOrigin = rows
            .slice(0, currentRowIdx)
            .reverse()
            .find((r) => r.competitor);
          if (prevOrigin) {
            const prevModeRows = getModeRows(prevOrigin);
            if (prevModeRows.length > 0) {
              const targetKey = getPriceInputKey(
                prevOrigin,
                prevModeRows[prevModeRows.length - 1].key,
                selectedMonths[selectedMonths.length - 1],
                "full",
              );
              const targetInput = priceInputRefs.current[targetKey];
              if (targetInput) {
                event.preventDefault();
                targetInput.focus();
              }
            }
          }
          return;
        }
      }

      // Within-origin navigation (horizontal-first: move across months before changing mode row)
      let targetModeIndex: number;
      let targetColIndex: number;

      if (!event.shiftKey) {
        if (!isLastColumn) {
          // Move right to the next month, same mode row
          targetModeIndex = currentModeIndex;
          targetColIndex = currentColIndex + 1;
        } else if (!isLastMode) {
          // Reached the last month — wrap to first month of the next mode row
          targetModeIndex = currentModeIndex + 1;
          targetColIndex = 0;
        } else {
          return;
        }
      } else if (!isFirstColumn) {
        // Move left to the previous month, same mode row
        targetModeIndex = currentModeIndex;
        targetColIndex = currentColIndex - 1;
      } else if (!isFirstMode) {
        // Reached the first month — wrap to last month of the previous mode row
        targetModeIndex = currentModeIndex - 1;
        targetColIndex = rowColumns.length - 1;
      } else {
        return;
      }

      const targetColumn = rowColumns[targetColIndex];
      const targetMode = columnModeRows[targetModeIndex];
      const targetKey = getPriceInputKey(
        origin,
        targetMode.key,
        targetColumn.month,
        targetColumn.half,
      );
      const targetInput = priceInputRefs.current[targetKey];
      if (!targetInput) return;

      event.preventDefault();
      targetInput.focus();
    },
    [
      editingProductKey,
      editingRowKey,
      getModeRows,
      getPriceInputKey,
      isEditingPrices,
      rows,
      selectedMonths,
      selectedProduct,
      startEditingRow,
    ],
  );

  const getSplitMonthsForRow = useCallback(
    (origin: IFavoriteLocationExtension) => {
      if (origin.competitor) {
        return [];
      }
      const rowKey = getRowKey(origin);
      if (
        isEditingPrices &&
        editingRowKey === rowKey &&
        editingProductKey === selectedProduct
      ) {
        return editingSplitMonths;
      }

      // Return unsaved splits set during this edit session for non-active rows.
      if (isEditingPrices && pendingSplitMonthsByRow[rowKey] !== undefined) {
        return pendingSplitMonthsByRow[rowKey];
      }

      return (
        editedSplitMonthsByProduct[selectedProduct]?.[rowKey] ??
        getSplitMonthsFromOrigin(origin)
      );
    },
    [
      editedSplitMonthsByProduct,
      editingProductKey,
      editingRowKey,
      editingSplitMonths,
      getSplitMonthsFromOrigin,
      isEditingPrices,
      pendingSplitMonthsByRow,
      selectedProduct,
    ],
  );

  const activeSplitMonths = useMemo(() => {
    return sortMonthsByTimeline(
      filteredRows.flatMap(getSplitMonthsForRow),
      currentMonth,
    );
  }, [currentMonth, filteredRows, getSplitMonthsForRow]);

  const splitMonthKeysForHeader = useMemo(() => {
    const currentYear = new Date().getFullYear();

    return Array.from(
      new Set(
        activeSplitMonths
          .map((monthLabel) => {
            const monthIndex = MONTHS.indexOf(monthLabel);
            if (monthIndex < 0) {
              return null;
            }

            const year =
              monthIndex >= currentMonth ? currentYear : currentYear + 1;
            return `${year}-${String(monthIndex + 1).padStart(2, "0")}`;
          })
          .filter((key): key is string => Boolean(key)),
      ),
    );
  }, [activeSplitMonths, currentMonth]);

  // Publish live split-month keys whenever they change. No cleanup here — calling
  // clearOriginManagerLiveSplitMonthKeys inside the same effect creates an infinite
  // loop because clearing updates the store, which causes parent re-renders, which
  // re-creates prop arrays, which recomputes splitMonthKeysForHeader, retriggering
  // this effect. The unmount-only effect below handles the final clear safely.
  const splitMonthKeysSerializedRef = useRef<string>("");
  useEffect(() => {
    if (!selectedProduct) {
      return;
    }
    const serialized = splitMonthKeysForHeader.slice().sort().join(",");
    if (serialized === splitMonthKeysSerializedRef.current) {
      return;
    }
    splitMonthKeysSerializedRef.current = serialized;
    setOriginManagerLiveSplitMonthKeys(
      selectedProduct,
      splitMonthKeysForHeader,
    );
  }, [
    selectedProduct,
    setOriginManagerLiveSplitMonthKeys,
    splitMonthKeysForHeader,
  ]);

  // Clear live split-month keys only on unmount to avoid triggering re-renders
  // during normal operation.
  const clearOriginManagerLiveSplitMonthKeysRef = useRef(
    clearOriginManagerLiveSplitMonthKeys,
  );
  const selectedProductRef = useRef(selectedProduct);
  useEffect(() => {
    clearOriginManagerLiveSplitMonthKeysRef.current =
      clearOriginManagerLiveSplitMonthKeys;
  }, [clearOriginManagerLiveSplitMonthKeys]);
  useEffect(() => {
    selectedProductRef.current = selectedProduct;
  }, [selectedProduct]);
  // (highlights persist across product switches — cleared only on page refresh or panel close)
  useEffect(() => {
    return () => {
      clearOriginManagerLiveSplitMonthKeysRef.current(
        selectedProductRef.current,
      );
    };
  }, []);

  const editingOrigin = useMemo(
    () => rows.find((origin) => getRowKey(origin) === editingRowKey) ?? null,
    [editingRowKey, rows],
  );

  const handleCancelSaveDiscard = useCallback(() => {
    setShowSaveDiscard(false);
    setPendingRowKey(null);
    setPendingProductKey(null);
  }, []);

  const handleRevertEditingPrices = useCallback(() => {
    if (editingRowKey) {
      const editingOriginId = editingRowKey.split("-").slice(1).join("-");
      const isNewlyAdded = (newlyAddedOrigins[selectedProduct] ?? []).some(
        (origin) => origin.location.id === editingOriginId,
      );
      if (isNewlyAdded) {
        setNewlyAddedOriginsByProduct((prev) => ({
          ...prev,
          [selectedProduct]: (prev[selectedProduct] ?? []).filter(
            (origin) => origin.location.id !== editingOriginId,
          ),
        }));
        setEditingNewlyAddedIds((prev) => {
          const next = new Set(prev);
          next.delete(editingOriginId);
          return next;
        });
      }
    }
    setDraftPrices(editedPricesByProduct[selectedProduct] ?? {});
    setDraftPriceShowAs(editedPriceShowAsByProduct[selectedProduct] ?? {});
    dirtyDraftPricesRef.current = {};
    dirtyDraftPriceShowAsRef.current = {};
    dirtyPriceKeysRef.current = new Set();
    setEditingSplitMonths([]);
    setPendingSplitMonthsByRow({});
    setIsEditingPrices(false);
    setEditingProductKey(null);
    setEditingRowKey(null);
    setActiveCellKey(null);
    setShowSaveDiscard(false);
    setPendingRowKey(null);
    setPendingProductKey(null);
  }, [
    editedPriceShowAsByProduct,
    editedPricesByProduct,
    editingRowKey,
    newlyAddedOrigins,
    selectedProduct,
    setEditingNewlyAddedIds,
    setNewlyAddedOriginsByProduct,
  ]);

  const handleRevertEditingPricesRef = useRef(handleRevertEditingPrices);
  useEffect(() => {
    handleRevertEditingPricesRef.current = handleRevertEditingPrices;
  });

  useEffect(() => {
    if (!isEditingPrices || showSaveDiscard) {
      return undefined;
    }

    const handleEscDiscard = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }
      event.preventDefault();
      handleRevertEditingPricesRef.current();
    };

    document.addEventListener("keydown", handleEscDiscard);
    return () => {
      document.removeEventListener("keydown", handleEscDiscard);
    };
  }, [isEditingPrices, showSaveDiscard]);

  const handleDoneEditingPrices = useCallback(
    async (overrides?: {
      prices?: Record<string, string>;
      showAs?: Record<string, FavoritePriceShowAs>;
      exitEditMode?: boolean;
    }) => {
      const exitEditMode = overrides?.exitEditMode ?? true;
      // Merge in-flight overrides (e.g. Discuss/No Offer clicks) that haven't landed in state yet.
      const effectiveDraftPrices = {
        ...draftPrices,
        ...dirtyDraftPricesRef.current,
        ...(overrides?.prices ?? {}),
      };
      const effectiveDraftPriceShowAs = {
        ...draftPriceShowAs,
        ...dirtyDraftPriceShowAsRef.current,
        ...(overrides?.showAs ?? {}),
      };

      // Capture keys with entered prices before resetting draft state
      const keysToHighlight = new Set<string>();
      Object.entries(effectiveDraftPrices).forEach(([key, value]) => {
        if (value.trim() !== "") keysToHighlight.add(key);
      });
      Object.keys(effectiveDraftPriceShowAs).forEach((key) =>
        keysToHighlight.add(key),
      );

      const marketer = getMarketerIdentifier();
      if (!editingProductKey) {
        setIsEditingPrices(false);
        return;
      }
      if (!marketer) {
        console.error(
          "Cannot save favorite origin prices: marketer identity is unavailable.",
        );
        return;
      }
      const saveProductKey = editingProductKey;
      const saveRowKey = editingRowKey;
      const editingOriginId = editingRowKey?.split("-").slice(1).join("-");
      const clearNewlyAddedForCurrentRow = editingOriginId
        ? (newlyAddedOrigins[saveProductKey] ?? []).some(
            (origin) => origin.location.id === editingOriginId,
          )
        : false;

      const editedOrigins = rows.map((origin) => {
        const isEditingOrigin = getRowKey(origin) === editingRowKey;

        // Update the favorite months with any changes made in the draft prices or split month settings.
        const updatedMonths = origin.months.map((data) => {
          const { parsedMonth, periods } = data;
          const { month, year } = parsedMonth;
          const chip = MONTHS[month - 1];
          const lastDay = new Date(year, month, 0).getDate().toString();

          let isSplitMonth = data.split;
          if (origin.competitor) {
            isSplitMonth = false;
          } else if (chip && isEditingOrigin) {
            isSplitMonth = editingSplitMonths.includes(chip);
          }
          const newPeriods = (isSplitMonth ? [0, 1] : [0]).map(
            (periodIndex) => {
              // Only treat a period as "existing" when the source data actually has an
              // entry at this index. Falling back to periods[0] here would make a
              // newly-split second period reuse the first period's id (and its price
              // ids), so saving 2H would silently overwrite 1H's price record.
              const sourcePeriod = periods[periodIndex] ?? null;
              let periodHalf: MonthHalf;
              if (!isSplitMonth) {
                periodHalf = "full";
              } else if (periodIndex === 0) {
                periodHalf = "h1";
              } else {
                periodHalf = "h2";
              }

              const getUpdatedPrice = (
                modeKey: string,
                existing: IFavoritePriceValue | null,
              ): IFavoritePriceValue | null => {
                if (!chip) return existing;
                const key = getPriceInputKey(origin, modeKey, chip, periodHalf);
                const draft = effectiveDraftPrices[key];
                const draftShowAs = effectiveDraftPriceShowAs[key];
                if (draft === undefined && draftShowAs === undefined) {
                  return existing;
                }
                const showAs = draftShowAs ?? existing?.showAs ?? "PRICE";
                if (showAs === "DISCUSS" || showAs === "NO_OFFER") {
                  return existing
                    ? { ...existing, price: null, showAs }
                    : {
                        price: null,
                        showAs,
                        id: "",
                        updatedAt: "N/A",
                        updatedBy: "N/A",
                      };
                }
                const price = parsePriceInput(draft);
                return existing
                  ? { ...existing, price, showAs }
                  : {
                      price,
                      showAs,
                      id: "",
                      updatedAt: "N/A",
                      updatedBy: "N/A",
                    };
              };

              const fallbackPeriod = periods[0];
              const startDate =
                month > 0
                  ? `${year}-${month.toString().padStart(2, "0")}-${
                      periodIndex === 0 ? "01" : "16"
                    }`
                  : sourcePeriod?.startDate ?? fallbackPeriod?.startDate;
              const endDate =
                month > 0
                  ? `${year}-${month.toString().padStart(2, "0")}-${
                      isSplitMonth && periodIndex === 0 ? "15" : lastDay
                    }`
                  : sourcePeriod?.endDate ?? fallbackPeriod?.endDate;

              return {
                ...(sourcePeriod ?? {
                  startDate,
                  endDate,
                  posted: null,
                  truck: null,
                  rail: null,
                }),
                startDate,
                endDate,
                posted: getUpdatedPrice("posted", sourcePeriod?.posted ?? null),
                truck: getUpdatedPrice("truck", sourcePeriod?.truck ?? null),
                rail: getUpdatedPrice("rail", sourcePeriod?.rail ?? null),
              } as IFavoritePeriod;
            },
          );

          return {
            ...data,
            split: isSplitMonth,
            firstPeriod: newPeriods[0],
            secondPeriod: isSplitMonth ? newPeriods[1] : null,
            periods: newPeriods,
          } as IFavoriteMonthExtension;
        });

        // For any selected month that has a draft price but no existing entry in
        // origin.months, create a new month entry so that price is included in the
        // save payload. Without this, prices typed into "new" month columns (months
        // the server never returned data for) are silently dropped from the API call.
        const coveredMonthNums = new Set(
          updatedMonths
            .map((m) => m.parsedMonth.month)
            .filter((n) => Number.isFinite(n) && n > 0),
        );
        const currentYear = new Date().getFullYear();
        const extraMonths: IFavoriteMonthExtension[] = [];
        selectedMonths.forEach((selectedMonthLabel) => {
          const selectedMonthIdx = MONTHS.indexOf(selectedMonthLabel);
          if (selectedMonthIdx < 0) return;
          const selectedMonthNum = selectedMonthIdx + 1;
          if (coveredMonthNums.has(selectedMonthNum)) return;

          const modeKeys = ["posted", "truck", "rail"] as const;
          const isSplitMonth =
            isEditingOrigin && editingSplitMonths.includes(selectedMonthLabel);
          const halves: MonthHalf[] = isSplitMonth ? ["h1", "h2"] : ["full"];
          const hasDraft = modeKeys.some((modeKey) => {
            return halves.some((half) => {
              const key = getPriceInputKey(
                origin,
                modeKey,
                selectedMonthLabel,
                half,
              );
              return (
                effectiveDraftPrices[key] !== undefined ||
                effectiveDraftPriceShowAs[key] !== undefined
              );
            });
          });
          if (!hasDraft) return;

          const selectedYear =
            selectedMonthIdx >= currentMonth ? currentYear : currentYear + 1;
          const selectedLastDay = new Date(selectedYear, selectedMonthNum, 0)
            .getDate()
            .toString();

          const buildExtraPrice = (
            modeKey: string,
            half: MonthHalf,
          ): IFavoritePriceValue | null => {
            const key = getPriceInputKey(
              origin,
              modeKey,
              selectedMonthLabel,
              half,
            );
            const draft = effectiveDraftPrices[key];
            const draftSA = effectiveDraftPriceShowAs[key];
            if (draft === undefined && draftSA === undefined) return null;
            const showAs: FavoritePriceShowAs = draftSA ?? "PRICE";
            if (showAs === "DISCUSS" || showAs === "NO_OFFER") {
              return {
                price: null,
                showAs,
                id: "",
                updatedAt: "N/A",
                updatedBy: "N/A",
              };
            }
            return {
              price: parsePriceInput(draft),
              showAs,
              id: "",
              updatedAt: "N/A",
              updatedBy: "N/A",
            };
          };

          const periods = halves.map(
            (half) =>
              ({
                startDate: `${selectedYear}-${selectedMonthNum
                  .toString()
                  .padStart(2, "0")}-${half === "h2" ? "16" : "01"}`,
                endDate: `${selectedYear}-${selectedMonthNum
                  .toString()
                  .padStart(2, "0")}-${half === "h1" ? "15" : selectedLastDay}`,
                posted: buildExtraPrice("posted", half),
                truck: buildExtraPrice("truck", half),
                rail: buildExtraPrice("rail", half),
                infos: [],
                id: "",
              } as IFavoritePeriod),
          );

          extraMonths.push({
            id: "",
            month: `${selectedYear}-${selectedMonthNum
              .toString()
              .padStart(2, "0")}-01`,
            split: isSplitMonth,
            firstPeriod: periods[0],
            secondPeriod: isSplitMonth ? periods[1] : null,
            periods,
            parsedMonth: {
              month: selectedMonthNum,
              year: selectedYear,
              day: 1,
            },
          });
        });

        return {
          ...origin,
          months: [...updatedMonths, ...extraMonths],
        } as IFavoriteLocationExtension;
      });
      const sourceOrigin = rows.find(
        (origin) => getRowKey(origin) === saveRowKey,
      );
      const editedOrigin = editedOrigins.find(
        (origin) => getRowKey(origin) === saveRowKey,
      );

      const getRowKeyFromPriceKey = (key: string) => {
        const parts = key.split("-");
        // Trailing segments are mode-month-half-product; everything before that is the row key.
        return parts.slice(0, parts.length - 4).join("-");
      };

      const overrideKeys = new Set<string>([
        ...Object.keys(overrides?.prices ?? {}),
        ...Object.keys(overrides?.showAs ?? {}),
      ]);
      const activeRowPrefix = sourceOrigin
        ? `${sourceOrigin.competitor ? "competitor" : "koch"}-${
            sourceOrigin.location.id
          }-`
        : null;
      const changedPriceKeys = new Set<string>([
        ...dirtyPriceKeysRef.current,
        ...overrideKeys,
      ]);

      const priceMutationKeys = Array.from(changedPriceKeys).filter(
        (key) =>
          (activeRowPrefix && key.startsWith(activeRowPrefix)) ||
          overrideKeys.has(key),
      );

      priceMutationKeys.forEach((priceKey) => {
        delete dirtyDraftPricesRef.current[priceKey];
        delete dirtyDraftPriceShowAsRef.current[priceKey];
        dirtyPriceKeysRef.current.delete(priceKey);
      });

      setPendingOrderSaveByProduct((prev) => ({
        ...prev,
        [selectedProduct]: false,
      }));

      setEditedPricesByProduct((prev) => ({
        ...prev,
        [saveProductKey]: {
          ...(prev[saveProductKey] ?? {}),
          ...effectiveDraftPrices,
        },
      }));
      setEditedPriceShowAsByProduct((prev) => ({
        ...prev,
        [saveProductKey]: {
          ...(prev[saveProductKey] ?? {}),
          ...effectiveDraftPriceShowAs,
        },
      }));
      if (saveRowKey) {
        setEditedSplitMonthsByProduct((prev) => ({
          ...prev,
          [saveProductKey]: {
            ...(prev[saveProductKey] ?? {}),
            [saveRowKey]: [...editingSplitMonths],
          },
        }));
      }
      setNewlyAddedOriginsByProduct((prev) => {
        const currentNewRows = prev[saveProductKey] ?? [];
        if (currentNewRows.length === 0) {
          return prev;
        }

        const editedByKey = new Map<string, IFavoriteLocationExtension>();
        editedOrigins.forEach((origin) => {
          const key = `${origin.competitor ? "competitor" : "koch"}-${
            origin.location.id
          }`;
          editedByKey.set(key, origin);
        });

        return {
          ...prev,
          [saveProductKey]: currentNewRows.map((origin) => {
            const key = `${origin.competitor ? "competitor" : "koch"}-${
              origin.location.id
            }`;
            return editedByKey.get(key) ?? origin;
          }),
        };
      });
      if (clearNewlyAddedForCurrentRow && editingOriginId) {
        setEditingNewlyAddedIds((prev) => {
          const next = new Set(prev);
          next.delete(editingOriginId);
          return next;
        });
      }

      if (keysToHighlight.size > 0) {
        setRecentlySavedKeys((prev) => {
          const next = new Set(prev);
          keysToHighlight.forEach((k) => next.add(k));
          return next;
        });
      }
      if (exitEditMode) {
        setIsEditingPrices(false);
        setEditingProductKey(null);
        setEditingRowKey(null);
        setActiveCellKey(null);
        setEditingSplitMonths([]);
        setPendingSplitMonthsByRow({});
      }
      setJustAddedByProduct((prev) => ({
        ...prev,
        [saveProductKey]: false,
      }));

      const persistPriceEditsInBackground = async () => {
        try {
          if (sourceOrigin && editedOrigin) {
            await persistSplitStateDelta(marketer, sourceOrigin, editedOrigin);
          }

          await Promise.all(
            priceMutationKeys.map(async (priceKey) => {
              const rowKeyForKey = getRowKeyFromPriceKey(priceKey);
              const targetEditedOrigin =
                rowKeyForKey === saveRowKey && editedOrigin
                  ? editedOrigin
                  : editedOrigins.find(
                      (origin) => getRowKey(origin) === rowKeyForKey,
                    );
              if (!targetEditedOrigin) {
                return;
              }

              const parts = priceKey.split("-");
              const half = parts[parts.length - 2] as MonthHalf;
              const monthLabel = parts[parts.length - 3];
              const mode = parts[parts.length - 4] as
                | "posted"
                | "truck"
                | "rail";

              const monthEntry = targetEditedOrigin.months.find(
                (date) => MONTHS[date.parsedMonth.month - 1] === monthLabel,
              );
              if (!monthEntry) {
                return;
              }

              const periodIndex = half === "h2" ? 1 : 0;
              const period = monthEntry.periods[periodIndex];
              if (!period) {
                return;
              }

              await persistPeriodPrice({
                marketer,
                product: selectedProduct,
                editedOrigin: targetEditedOrigin,
                monthEntry,
                period,
                mode,
              });
            }),
          );

          const refreshedFavorites = await refreshFavorites(marketer);

          onSaveComplete?.();

          if (refreshedFavorites.length) {
            setNewlyAddedOriginsByProduct((prev) => ({
              ...prev,
              [saveProductKey]: [],
            }));

            if (saveRowKey) {
              setEditedSplitMonthsByProduct((prev) => {
                const productSplits = { ...(prev[saveProductKey] ?? {}) };
                delete productSplits[saveRowKey];
                return { ...prev, [saveProductKey]: productSplits };
              });
            }
          }
        } catch (error) {
          console.error("Failed to save favorite origin prices.", error);
          failedNotification(
            "Failed to save prices",
            "We could not save these price changes. Please try again.",
          );
        }
      };

      trackBackgroundOp(persistPriceEditsInBackground());
    },
    [
      currentMonth,
      draftPriceShowAs,
      draftPrices,
      editingProductKey,
      editingRowKey,
      editingSplitMonths,
      failedNotification,
      getMarketerIdentifier,
      getPriceInputKey,
      newlyAddedOrigins,
      onSaveComplete,
      refreshFavorites,
      rows,
      selectedMonths,
      selectedProduct,
      setEditingNewlyAddedIds,
      setJustAddedByProduct,
      setNewlyAddedOriginsByProduct,
      setPendingOrderSaveByProduct,
      trackBackgroundOp,
    ],
  );

  // Click-away: save prices when user clicks outside the origin manager panel while editing
  const handleDoneEditingPricesRef = useRef(handleDoneEditingPrices);
  useEffect(() => {
    handleDoneEditingPricesRef.current = handleDoneEditingPrices;
  });

  useEffect(() => {
    if (!isEditingPrices || showSaveDiscard) {
      return undefined;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (target instanceof Element) {
        if (
          target.closest(".dom-filter-card") ||
          target.closest(".dom-header-row")
        ) {
          handleDoneEditingPricesRef.current();
          return;
        }

        // These controls have their own unsaved-edits flow; do not autosave first.
        if (
          target.closest(".origin-manager-close-btn") ||
          target.closest(".home-vertical-sidebar-button") ||
          target.closest(".dom-save-discard-overlay")
        ) {
          return;
        }
      }
      if (panelRef.current && panelRef.current.contains(target)) {
        return;
      }
      handleDoneEditingPricesRef.current();
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditingPrices, showSaveDiscard, panelRef]);

  // Global exit: leave edit mode when the user switches away from the tab or
  // closes it, rather than only on blur/action (those now persist without exiting).
  useEffect(() => {
    if (!isEditingPrices || showSaveDiscard) {
      return undefined;
    }

    const exitEditingOnGlobalEvent = () => {
      handleDoneEditingPricesRef.current();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        exitEditingOnGlobalEvent();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", exitEditingOnGlobalEvent);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", exitEditingOnGlobalEvent);
    };
  }, [isEditingPrices, showSaveDiscard]);

  const handlePriceInputBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (!isEditingPrices || showSaveDiscard) {
        return;
      }

      const nextFocused = event.relatedTarget as Node | null;
      if (
        nextFocused &&
        event.currentTarget.parentElement?.contains(nextFocused)
      ) {
        return;
      }

      if (
        nextFocused instanceof Element &&
        nextFocused.id === "cancel-editing-prices"
      ) {
        return;
      }

      // Persist the value but stay in edit mode; only global outside-click/tab
      // events (handled above) actually exit editing.
      window.setTimeout(() => {
        handleDoneEditingPricesRef.current({ exitEditMode: false });
      }, 0);
    },
    [isEditingPrices, showSaveDiscard],
  );

  const handlePriceShowAsChange = useCallback(
    (
      origin: IFavoriteLocationExtension,
      modeKey: string,
      month: string,
      half: MonthHalf,
      showAs: FavoritePriceShowAs,
    ) => {
      if (origin.competitor && showAs === "DISCUSS") {
        return;
      }
      const priceKey = getPriceInputKey(origin, modeKey, month, half);
      dirtyDraftPriceShowAsRef.current[priceKey] = showAs;
      dirtyPriceKeysRef.current.add(priceKey);
      setRecentlySavedKeys((prev) => {
        const next = new Set(prev);
        next.add(priceKey);
        return next;
      });
      setDraftPriceShowAs((prev) => ({
        ...prev,
        [priceKey]: showAs,
      }));
      if (showAs === "DISCUSS") {
        dirtyDraftPricesRef.current[priceKey] = "Discuss";
        setDraftPrices((prev) => ({
          ...prev,
          [priceKey]: "Discuss",
        }));
        return;
      }
      if (showAs === "NO_OFFER") {
        dirtyDraftPricesRef.current[priceKey] = "No Offer";
        setDraftPrices((prev) => ({
          ...prev,
          [priceKey]: "No Offer",
        }));
        return;
      }

      const raw = draftPrices[priceKey];
      const parsed = raw === undefined ? Number.NaN : Number(raw);
      const normalizedPrice = Number.isFinite(parsed) ? String(parsed) : "";
      dirtyDraftPricesRef.current[priceKey] = normalizedPrice;
      setDraftPrices((prev) => ({
        ...prev,
        [priceKey]: normalizedPrice,
      }));

      handleDoneEditingPrices({
        prices: { [priceKey]: normalizedPrice },
        showAs: { [priceKey]: "PRICE" },
        exitEditMode: false,
      }).then(() => {
        priceInputRefs.current[priceKey]?.focus();
      });
    },
    [draftPrices, getPriceInputKey, handleDoneEditingPrices],
  );

  // Discuss/No Offer save immediately but stay in edit mode; only global
  // outside-click/tab events exit editing.
  const handlePriceShowAsChangeAndSave = useCallback(
    (
      origin: IFavoriteLocationExtension,
      modeKey: string,
      month: string,
      half: MonthHalf,
      showAs: "DISCUSS" | "NO_OFFER",
    ) => {
      if (origin.competitor && showAs === "DISCUSS") {
        return;
      }
      const priceKey = getPriceInputKey(origin, modeKey, month, half);
      const priceValue = showAs === "DISCUSS" ? "Discuss" : "No Offer";
      dirtyDraftPriceShowAsRef.current[priceKey] = showAs;
      dirtyDraftPricesRef.current[priceKey] = priceValue;
      dirtyPriceKeysRef.current.add(priceKey);

      setRecentlySavedKeys((prev) => {
        const next = new Set(prev);
        next.add(priceKey);
        return next;
      });
      setDraftPriceShowAs((prev) => ({ ...prev, [priceKey]: showAs }));
      setDraftPrices((prev) => ({ ...prev, [priceKey]: priceValue }));

      handleDoneEditingPrices({
        prices: { [priceKey]: priceValue },
        showAs: { [priceKey]: showAs },
        exitEditMode: false,
      });
    },
    [getPriceInputKey, handleDoneEditingPrices],
  );

  const handleToggleSplitMonth = useCallback(
    async (month: string) => {
      if (!editingOrigin || editingOrigin.competitor || !isEditingPrices) {
        return;
      }

      const isActive = editingSplitMonths.includes(month);
      if (isActive) {
        setEditingSplitMonths((prev) => prev.filter((item) => item !== month));
        return;
      }

      setEditingSplitMonths((prev) => [...prev, month]);

      const modeKeys: Array<ModeValueRow["key"]> = ["posted", "truck", "rail"];
      const sourceRows = getModeRows(editingOrigin, month, "full");
      setDraftPrices((prev) => {
        const next = { ...prev };
        modeKeys.forEach((modeKey) => {
          const fullKey = getPriceInputKey(
            editingOrigin,
            modeKey,
            month,
            "full",
          );
          const h1Key = getPriceInputKey(editingOrigin, modeKey, month, "h1");
          const modeRow = sourceRows.find((row) => row.key === modeKey);
          let fallbackRaw: string | undefined;
          if (fullKey in next) {
            fallbackRaw = next[fullKey];
          } else if (modeRow?.value !== null && modeRow?.value !== undefined) {
            fallbackRaw = String(modeRow.value);
          }
          if (fallbackRaw !== undefined) {
            if (!(h1Key in next)) next[h1Key] = fallbackRaw;
          }
        });
        return next;
      });

      setDraftPriceShowAs((prev) => {
        const next = { ...prev };
        modeKeys.forEach((modeKey) => {
          const fullKey = getPriceInputKey(
            editingOrigin,
            modeKey,
            month,
            "full",
          );
          const h1Key = getPriceInputKey(editingOrigin, modeKey, month, "h1");
          const modeRow = sourceRows.find((row) => row.key === modeKey);
          const fallbackShowAs = next[fullKey] ?? modeRow?.showAs;
          if (fallbackShowAs !== undefined && fallbackShowAs !== null) {
            if (next[h1Key] === undefined) next[h1Key] = fallbackShowAs;
          }
        });
        return next;
      });
    },
    [
      editingOrigin,
      editingSplitMonths,
      getModeRows,
      getPriceInputKey,
      isEditingPrices,
    ],
  );

  const handleDiscardEdits = useCallback(() => {
    const nextKey = pendingRowKey;
    const nextProduct = pendingProductKey;
    const nextOrigin = nextKey
      ? rows.find((r) => getRowKey(r) === nextKey)
      : undefined;
    handleRevertEditingPrices();
    if (nextOrigin) {
      startEditingRow(nextOrigin);
      return;
    }
    if (nextProduct) {
      setPendingProductKey(null);
      onProductSelect(nextProduct);
      return;
    }

    triggerOriginManagerUnsavedNavResolution("discard");
    if (originManagerUnsavedNavAttemptSource === "close") {
      savePendingOrderInBackground(
        selectedProduct,
        savedOrigins,
        newlyAddedOrigins,
        deletedRowKeysByProduct,
      );
      triggerOriginManagerCloseRequest();
    }
  }, [
    deletedRowKeysByProduct,
    handleRevertEditingPrices,
    newlyAddedOrigins,
    onProductSelect,
    originManagerUnsavedNavAttemptSource,
    pendingProductKey,
    pendingRowKey,
    rows,
    savedOrigins,
    savePendingOrderInBackground,
    selectedProduct,
    startEditingRow,
    triggerOriginManagerCloseRequest,
    triggerOriginManagerUnsavedNavResolution,
  ]);

  const handleSaveEdits = useCallback(async () => {
    const nextKey = pendingRowKey;
    const nextProduct = pendingProductKey;
    const nextOrigin = nextKey
      ? rows.find((r) => getRowKey(r) === nextKey)
      : undefined;
    setShowSaveDiscard(false);
    setPendingRowKey(null);
    setPendingProductKey(null);
    await handleDoneEditingPrices();

    if (nextOrigin) {
      startEditingRow(nextOrigin);
      return;
    }
    if (nextProduct) {
      onProductSelect(nextProduct);
      return;
    }

    triggerOriginManagerUnsavedNavResolution("save");
    if (originManagerUnsavedNavAttemptSource === "close") {
      savePendingOrderInBackground(
        selectedProduct,
        savedOrigins,
        newlyAddedOrigins,
        deletedRowKeysByProduct,
      );
      triggerOriginManagerCloseRequest();
    }
  }, [
    deletedRowKeysByProduct,
    handleDoneEditingPrices,
    newlyAddedOrigins,
    onProductSelect,
    originManagerUnsavedNavAttemptSource,
    pendingProductKey,
    pendingRowKey,
    rows,
    savedOrigins,
    savePendingOrderInBackground,
    selectedProduct,
    startEditingRow,
    triggerOriginManagerCloseRequest,
    triggerOriginManagerUnsavedNavResolution,
  ]);

  return {
    editedPricesByProduct,
    editedPriceShowAsByProduct,
    editedSplitMonthsByProduct,
    setEditedSplitMonthsByProduct,
    isEditingPrices,
    editingProductKey,
    draftPrices,
    setDraftPrices,
    draftPriceShowAs,
    setDraftPriceShowAs,
    recentlySavedKeys,
    editingSplitMonths,
    editingRowKey,
    activeCellKey,
    setActiveCellKey,
    pendingSplitMonthsByRow,
    pendingRowKey,
    pendingProductKey,
    setPendingRowKey,
    setPendingProductKey,
    showSaveDiscard,
    setShowSaveDiscard,
    setIsEditingPrices,
    setEditingProductKey,
    setEditingRowKey,
    setEditingSplitMonths,
    priceInputRefs,
    editingOrigin,
    getSplitMonthsForRow,
    activeSplitMonths,
    splitMonthKeysForHeader,
    getPriceInputKey,
    getPriceInputValue,
    getPriceShowAsValue,
    getDisplayPriceValue,
    handlePriceInputChange,
    handlePriceInputKeyDown,
    handlePriceInputBlur,
    handlePriceShowAsChange,
    handlePriceShowAsChangeAndSave,
    startEditingRow,
    handlePriceCellClick,
    handleCancelSaveDiscard,
    handleDiscardEdits,
    handleSaveEdits,
    handleToggleSplitMonth,
    handleRevertEditingPrices,
    handleDoneEditingPrices,
  };
};
